import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User, PinRequirements, TwoFactorRequiredError } from '../types';
import { useIdleTimeout } from '../hooks/useIdleTimeout';

interface AuthContextType {
    user: User | null;
    token: string | null;
    loading: boolean;
    loginWithPassword: (username: string, password: string) => Promise<User>;
    loginWithPin: (username: string, pin: string) => Promise<User>;
    verify2FALogin: (tempToken: string, code: string) => Promise<User>;
    verifyPin: (pin: string) => Promise<string>;
    getPinSettings: () => Promise<PinRequirements>;
    updatePinSettings: (settings: Partial<PinRequirements>) => Promise<PinRequirements>;
    logout: () => void;
    isAdmin?: boolean;
    userRole?: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // On mount, check if a valid session cookie exists
        const checkSession = async () => {
            try {
                const response = await axios.get(`${API}/auth/me`);
                setUser(response.data);
                setToken('cookie'); // sentinel value: auth lives in httpOnly cookie
            } catch {
                setUser(null);
                setToken(null);
            } finally {
                setLoading(false);
            }
        };
        checkSession();
    }, []);

    const loginWithPassword = async (username: string, password: string) => {
        const response = await axios.post(
            `${API}/auth/login`,
            { username, password },
            {
                headers: {
                    'X-Portal-Access-Token': localStorage.getItem('pharmacy_portal_access_token') || '',
                },
            }
        );
        if (response.data.requires_2fa) {
            throw new TwoFactorRequiredError(response.data.temp_token);
        }
        const { user: userData } = response.data;
        sessionStorage.setItem('labflo_username', userData.username || username);
        setToken('cookie');
        setUser(userData);
        return userData;
    };

    const loginWithPin = async (username: string, pin: string) => {
        const response = await axios.post(
            `${API}/auth/login`,
            { username, pin },
            {
                headers: {
                    'X-Portal-Access-Token': localStorage.getItem('pharmacy_portal_access_token') || '',
                },
            }
        );
        if (response.data.requires_2fa) {
            throw new TwoFactorRequiredError(response.data.temp_token);
        }
        const { user: userData } = response.data;
        sessionStorage.setItem('labflo_username', userData.username || username);
        setToken('cookie');
        setUser(userData);
        return userData;
    };

    const verify2FALogin = async (tempToken: string, code: string) => {
        const response = await axios.post(`${API}/auth/2fa/verify-login`, { temp_token: tempToken, code });
        const { user: userData } = response.data;
        sessionStorage.setItem('labflo_username', userData.username || '');
        setToken('cookie');
        setUser(userData);
        return userData;
    };

    const verifyPin = async (pin: string) => {
        const response = await axios.post(`${API}/auth/verify-pin`, { pin });
        return response.data.pin_token;
    };

    const updatePinSettings = async (settings: Partial<PinRequirements>) => {
        const response = await axios.put(`${API}/settings/pin`, settings);
        return response.data;
    };

    const getPinSettings = async () => {
        const response = await axios.get(`${API}/settings/pin`);
        return response.data;
    };

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API}/auth/logout`);
        } catch {
            // ignore
        }
        sessionStorage.removeItem('labflo_username');
        setToken(null);
        setUser(null);
    }, []);

    const handleIdleTimeout = useCallback(() => {
        if (user) {
            logout();
            toast.info('Session expired due to inactivity. Please sign in again.');
        }
    }, [user, logout]);

    // HIPAA: auto-logout after 15 minutes of inactivity
    useIdleTimeout(handleIdleTimeout, !!user);

    // HIPAA: auto-logout when browser back button navigates past login
    useEffect(() => {
        if (!user) return;
        // Push a guard state so pressing back triggers popstate
        window.history.pushState({ labflo_guard: true }, '');
        const handlePopState = (_e: PopStateEvent) => {
            // If user presses back and lands on a non-app page (login/portal), logout
            const path = window.location.pathname;
            if (path.includes('/login') || path.includes('/portal/') || path === '/') {
                logout();
                toast.info('Logged out for security. Please sign in again.');
            } else {
                // Re-push guard for next back press
                window.history.pushState({ labflo_guard: true }, '');
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [user, logout]);

    return (
        <AuthContext.Provider value={{ user, token, loading, loginWithPassword, loginWithPin, verify2FALogin, verifyPin, getPinSettings, updatePinSettings, logout, isAdmin: user?.is_admin, userRole: user?.role || (user?.is_admin ? 'admin' : 'pharmacy_tech') }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
